import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './holding.css';
import { CrowdProvider, useCrowd } from './context/CrowdContext';
import { HoldingTopTiles } from '../components/holding/HoldingTopTiles';
import { fmt } from './utils/crowdMath';
import type { HoldingRoom, HoldingComplex } from './types';
import { HoldingAreaBlueprintModal } from './HoldingAreaBlueprintModal';
import { HoldingAreaCCTVModal } from './HoldingAreaCCTVModal';
import {
  Warehouse,
  Video,
  Radio,
  Search,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
} from 'lucide-react';

const HoldingAreaViewInner: React.FC = () => {
  const { holdingComplexes, updateRoomGate, flushHoldingRoom } = useCrowd();

  // Active holding area for blueprint & details
  const [selectedAreaId, setSelectedAreaId] = useState<string>('HA-1');
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'dense' | 'critical'>('all');

  const [activeHoldingCam, setActiveHoldingCam] = useState<{
    camId: string;
    room: HoldingRoom;
    areaName: string;
    sector: string;
  } | null>(null);

  // The three engineering drawings supplied for the festival.
  const availableComplexes = useMemo(
    () => holdingComplexes.filter(ha => ['HA-1', 'HA-2', 'HA-3'].includes(ha.id)),
    [holdingComplexes]
  );

  const currentComplex: HoldingComplex = useMemo(() => {
    return (
      availableComplexes.find(ha => ha.id === selectedAreaId) ||
      availableComplexes[0] || {
        id: 'HA-1',
        name: 'Holding Area 1',
        te: 'హోల్డింగ్ ఏరియా 1',
        location: 'Kalyanamandapam Outer Ridge',
        sector: 'B-Holding',
        totalCapacity: 5850,
        declaredCapacity: 6000,
        rooms: [],
        cctvCount: 30,
        releaseCorridor: 'Inner Ring Corridor -> Queue Complex',
        status: 'active',
        gatesOpen: true
      }
    );
  }, [availableComplexes, selectedAreaId]);

  // Aggregate stats
  const totalRooms = useMemo(
    () => availableComplexes.reduce((acc, ha) => acc + ha.rooms.length, 0),
    [availableComplexes]
  );

  // Filter rooms for the table
  const filteredRooms = useMemo(() => {
    return currentComplex.rooms
      .map((rm, idx) => ({
        room: rm,
        originalIndex: idx,
        dimensions: rm.dimensions
      }))
      .filter(({ room }) => {
        const occPct = Math.round((room.currentCount / room.capacity) * 100);
        if (statusFilter === 'dense' && (occPct <= 50 || occPct > 80)) return false;
        if (statusFilter === 'critical' && occPct <= 80) return false;
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          return (
            room.id.toLowerCase().includes(q) ||
            room.name.toLowerCase().includes(q) ||
            (room.te && room.te.toLowerCase().includes(q))
          );
        }
        return true;
      });
  }, [currentComplex, statusFilter, searchQuery]);

  // Split rooms into Left & Right tables
  const splitMid = Math.ceil(filteredRooms.length / 2);
  const leftTableRooms = filteredRooms.slice(0, splitMid);
  const rightTableRooms = filteredRooms.slice(splitMid);

  // Density counts for current complex (0-50% normal, 50-80% dense, >80% critical)
  const normalCount = currentComplex.rooms.filter(r => r.live !== false && (r.currentCount / r.capacity) <= 0.5).length;
  const denseCount = currentComplex.rooms.filter(r => (r.currentCount / r.capacity) > 0.5 && (r.currentCount / r.capacity) <= 0.8).length;
  const criticalCount = currentComplex.rooms.filter(r => (r.currentCount / r.capacity) > 0.8).length;

  const handleOpenSurveillance = (camId: string, roomOrName: HoldingRoom | string, holdingAreaName?: string, sector?: string) => {
    if (typeof roomOrName === 'string') {
      const allRooms = holdingComplexes.flatMap(h => h.rooms);
      const found = allRooms.find(
        r => r.name === roomOrName || r.id === camId || (r.cams && r.cams.includes(camId))
      );
      const targetRoom = found || {
        id: camId,
        blueprintNumber: '—',
        name: roomOrName,
        te: 'హోల్డింగ్ గది',
        dimensions: "40'x20'",
        areaSqFt: 800,
        currentCount: 140,
        capacity: 200,
        status: 'normal',
        gateStatus: 'open',
        cams: [camId],
        coolingFans: true,
        waterAvailable: true,
        medicalPost: false,
        inflowRate: 35,
        outflowRate: 32
      };
      setActiveHoldingCam({
        camId,
        room: targetRoom,
        areaName: holdingAreaName || currentComplex.name,
        sector: sector || currentComplex.sector
      });
    } else {
      setActiveHoldingCam({
        camId,
        room: roomOrName,
        areaName: holdingAreaName || currentComplex.name,
        sector: sector || currentComplex.sector
      });
    }
  };

  const renderRoomTable = (items: typeof filteredRooms) => {
    if (items.length === 0) {
      return (
        <div className="py-6 text-center text-xs text-neutral-400 font-mono">
          No matching compartments
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg border border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#181b22]">
        <table className="w-full text-left text-xs sm:text-[13px] border-collapse">
          <thead>
            <tr className="bg-neutral-50/80 dark:bg-[#14171f] text-neutral-500 dark:text-neutral-400 text-[11px] sm:text-xs font-mono uppercase tracking-wider border-b border-neutral-200/80 dark:border-[#222733]">
              <th className="py-2.5 px-3 font-semibold">Block / Room</th>
              <th className="py-2.5 px-2.5 font-semibold">Devotees / Cap</th>
              <th className="py-2.5 px-2.5 font-semibold">Occ %</th>
              <th className="py-2.5 px-2.5 font-semibold">Rates</th>
              <th className="py-2.5 px-2.5 font-semibold text-center">Gate</th>
              <th className="py-2.5 px-2.5 font-semibold text-right">CCTV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-[#202532] font-mono text-xs sm:text-[12.5px]">
            {items.map(({ room, originalIndex }) => {
              const occPct = Math.round((room.currentCount / room.capacity) * 100);
              const isFull = occPct > 80;
              const isDense = occPct > 50 && occPct <= 80;
              const primaryCam = room.cams[0] || `CAM-${currentComplex.id}-B${String(originalIndex + 1).padStart(2, '0')}-A`;

              const occBadge = isFull
                ? 'bg-rose-50 text-rose-700 border-rose-200/80 dark:bg-[#2c1217] dark:text-rose-300 dark:border-rose-900/50 font-bold'
                : isDense
                  ? 'bg-amber-50 text-amber-700 border-amber-200/80 dark:bg-[#2a2209] dark:text-amber-300 dark:border-amber-900/50 font-bold'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200/80 dark:bg-[#06281e] dark:text-emerald-300 dark:border-emerald-900/50 font-semibold';

              const barColor = isFull ? 'bg-rose-500' : isDense ? 'bg-amber-500' : 'bg-emerald-500';

              const rowHighlight = isFull
                ? 'bg-rose-50/25 dark:bg-[#2c1217]/15 hover:bg-rose-50/50 dark:hover:bg-[#2c1217]/35'
                : isDense
                  ? 'bg-amber-50/25 dark:bg-[#2a2209]/15 hover:bg-amber-50/50 dark:hover:bg-[#2a2209]/35'
                  : 'hover:bg-emerald-50/20 dark:hover:bg-[#06281e]/20';

              return (
                <tr
                  key={room.id}
                  className={`transition group ${rowHighlight}`}
                >
                  {/* Block / Room Name */}
                  <td className="py-2 px-3 font-sans">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-[#202532] border border-neutral-200/60 dark:border-[#2a3040] text-neutral-700 dark:text-neutral-300 font-bold">
                        B-{room.blueprintNumber}
                      </span>
                      <div className="truncate max-w-[120px] sm:max-w-[150px]">
                        <span className="text-xs sm:text-[13px] font-bold text-neutral-900 dark:text-neutral-100 truncate block">
                          {room.name}
                        </span>
                        <span className="text-[10px] text-neutral-400 font-normal truncate block">
                          {room.dimensions} · {fmt(room.areaSqFt)} sq ft ÷ 4 = {fmt(room.capacity)} seats
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Devotees / Cap + mini progress */}
                  <td className="py-2 px-2.5">
                    <div className="flex flex-col">
                      <div className="flex items-baseline gap-1">
                        <span className="font-bold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm" title={room.live === false ? 'No camera counting in this box yet' : undefined}>
                          {room.live === false ? '—' : fmt(room.currentCount)}
                        </span>
                        <span className="text-[10.5px] text-neutral-400">/{fmt(room.capacity)}</span>
                      </div>
                      <div className="w-16 h-1 rounded-full bg-neutral-200 dark:bg-[#262c3a] overflow-hidden mt-0.5">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                          style={{ width: `${Math.min(100, occPct)}%` }}
                        />
                      </div>
                    </div>
                  </td>

                  {/* Occ % */}
                  <td className="py-2 px-2.5">
                    {room.live === false ? (
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-sans border border-neutral-200 dark:border-[#2a3040] text-neutral-400">no data</span>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono border ${occBadge}`}>
                        {occPct}%
                      </span>
                    )}
                  </td>

                  {/* Micro Rates */}
                  <td className="py-2 px-2.5 text-[10.5px] text-neutral-500 dark:text-neutral-400">
                    <div className="flex flex-col leading-tight">
                      <span className="text-cyan-600 dark:text-cyan-400">{room.live === false ? '—' : `+${room.inflowRate}/m`}</span>
                      <span className="text-blue-600 dark:text-blue-400">{room.live === false ? '—' : `-${room.outflowRate}/m`}</span>
                    </div>
                  </td>

                  {/* Gate status */}
                  <td className="py-2 px-2.5 text-center">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${room.gateStatus === 'open'
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-500/20'
                          : room.gateStatus === 'metered'
                            ? 'bg-amber-50 text-amber-600 border border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-500/20'
                            : 'bg-rose-50 text-rose-600 border border-rose-200/80 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-500/20'
                        }`}
                    >
                      {room.gateStatus}
                    </span>
                  </td>

                  {/* CCTV Stream Action */}
                  <td className="py-2 px-2.5 text-right">
                    <button
                      onClick={() => handleOpenSurveillance(primaryCam, room, currentComplex.name, currentComplex.sector)}
                      className="px-2.5 py-1 rounded bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-500/30 text-blue-600 dark:text-blue-400 text-[11px] font-semibold inline-flex items-center gap-1.5 transition cursor-pointer active:scale-95 shadow-2xs"
                      title={`Stream live AI surveillance from ${primaryCam}`}
                    >
                      <Video className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span>CCTV</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-[#f4f5f8] dark:bg-[#111215] text-neutral-900 dark:text-neutral-100 font-sans space-y-2.5 transition-colors duration-200 mx-auto">
      {/* 1. Header Bar (Compact) */}
      <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-2.5 sm:px-3.5 sm:py-2.5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-neutral-100 dark:border-[#222733]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
              <Warehouse className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-2xl font-black text-neutral-900 dark:text-neutral-100 tracking-tight">
                  Holding Areas Command &amp; Operational Blueprint
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-500/20">
                  <Radio className="w-2.5 h-2.5 animate-pulse text-emerald-500" />
                  Live Feeds
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-neutral-100 dark:bg-[#202532] text-neutral-600 dark:text-neutral-400 border border-neutral-200/60 dark:border-[#2a3040]">
                  {totalRooms} Compartments
                </span>
                <Link to="/holding-mapping" className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-500/30 transition">
                  Cameras, boxes &amp; counting areas
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Area Switcher */}
          <div className="flex items-center gap-1 bg-neutral-100 dark:bg-[#14171f] p-1 rounded-lg border border-neutral-200/80 dark:border-[#262b37] self-start sm:self-auto shrink-0">
            {availableComplexes.map((ha, idx) => {
              const isSelected = selectedAreaId === ha.id;
              const areaDevotees = ha.rooms.reduce((acc, rm) => acc + rm.currentCount, 0);
              const occPct = Math.round((areaDevotees / ha.totalCapacity) * 100);

              return (
                <button
                  key={ha.id}
                  onClick={() => setSelectedAreaId(ha.id)}
                  className={`px-2.5 py-1 rounded-md text-xs sm:text-[13px] font-semibold transition cursor-pointer flex items-center gap-1.5 ${isSelected
                      ? 'bg-blue-500 text-white shadow-xs'
                      : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 hover:bg-neutral-200/60 dark:hover:bg-[#202532]'
                    }`}
                >
                  <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400'
                    }`}>
                    HA-{idx + 1}
                  </span>
                  <span>{ha.name.split('·')[0].trim()}</span>
                  <span className={`text-xs font-mono ${isSelected ? 'text-white/80' : 'text-neutral-400 dark:text-neutral-500'
                    }`}>
                    {occPct}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Key figures, live from /api/holding */}
        <HoldingTopTiles />
      </div>

      {/* 2. Interactive Operational Blueprint (Rendered Directly in Page) */}
      <div className="w-full">
        <HoldingAreaBlueprintModal
          isInline={true}
          holdingArea={currentComplex}
          selectedAreaId={selectedAreaId}
          onAreaChange={id => setSelectedAreaId(id)}
          onOpenCamera={(camId, roomName) => {
            handleOpenSurveillance(camId, roomName);
          }}
          onUpdateRoomGate={updateRoomGate}
          onFlushRoom={flushHoldingRoom}
        />
      </div>

      {/* 3. Collapsible Section: "More Details" / Split Table of Rooms */}
      <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl overflow-hidden shadow-xs">
        {/* Accordion Toggle Header */}
        <button
          onClick={() => setIsDetailsOpen(prev => !prev)}
          className="w-full p-3 sm:px-4 sm:py-3 bg-neutral-50/80 dark:bg-[#151821] hover:bg-neutral-100/80 dark:hover:bg-[#1d222e] border-b border-neutral-200/70 dark:border-[#222733] flex items-center justify-between gap-3 transition cursor-pointer select-none text-left"
          aria-expanded={isDetailsOpen}
        >
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400">
              <SlidersHorizontal className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm sm:text-base text-neutral-900 dark:text-neutral-100">
                  More Details &amp; Compartments Telemetry Breakdown
                </span>
                <span className="font-mono text-xs px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200/70 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-500/20 font-bold">
                  {currentComplex.rooms.length} Rooms in {currentComplex.id}
                </span>
              </div>
              <p className="text-xs sm:text-[13px] text-neutral-500 dark:text-neutral-400 font-normal mt-0.5">
                Click to {isDetailsOpen ? 'collapse' : 'view'} dual-column split breakdown with dimensions, occupancy rates, gate statuses, and AI CCTV controls
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Density breakdown badges */}
            <div className="hidden md:flex items-center gap-1.5 text-xs font-mono">
              <span className="px-2.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200/60 dark:bg-emerald-950/30 dark:text-emerald-400">
                {normalCount} Normal
              </span>
              <span className="px-2.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200/60 dark:bg-amber-950/30 dark:text-amber-400">
                {denseCount} Dense
              </span>
              <span className="px-2.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200/60 dark:bg-rose-950/30 dark:text-rose-400">
                {criticalCount} Critical
              </span>
            </div>

            <div className="flex items-center gap-1 text-xs sm:text-[13px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-3 py-1.5 rounded-lg border border-blue-200/70 dark:border-blue-500/20 shadow-2xs">
              <span>{isDetailsOpen ? 'Hide Details' : 'Show Details'}</span>
              {isDetailsOpen ? (
                <ChevronUp className="w-4 h-4 transition-transform duration-200" />
              ) : (
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              )}
            </div>
          </div>
        </button>

        {/* Accordion Body: Split Table */}
        {isDetailsOpen && (
          <div className="p-3 sm:p-4 space-y-3 animate-in fade-in duration-200">
            {/* Table Search & Filter Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-2 border-b border-neutral-100 dark:border-[#222733]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative w-full sm:w-60">
                  <Search className="w-4 h-4 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search compartment name / ID..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-8.5 pr-2.5 py-1.5 text-xs sm:text-[13px] rounded-lg bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/80 dark:border-[#262b37] text-neutral-800 dark:text-neutral-200 placeholder-neutral-400 focus:outline-hidden focus:border-blue-500 shadow-2xs"
                  />
                </div>

                {/* Filter buttons */}
                <div className="bg-neutral-50 dark:bg-[#14171f] p-1 rounded-lg inline-flex items-center gap-1 border border-neutral-200/80 dark:border-[#262b37]">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-2.5 py-1 rounded text-xs font-semibold transition cursor-pointer ${statusFilter === 'all'
                        ? 'bg-neutral-200 dark:bg-[#2a3142] text-neutral-900 dark:text-neutral-100 shadow-xs'
                        : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200'
                      }`}
                  >
                    All ({currentComplex.rooms.length})
                  </button>
                  <button
                    onClick={() => setStatusFilter('dense')}
                    className={`px-2.5 py-1 rounded text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer ${statusFilter === 'dense'
                        ? 'bg-amber-500 text-white shadow-xs'
                        : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                      }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    Dense (50-80%)
                  </button>
                  <button
                    onClick={() => setStatusFilter('critical')}
                    className={`px-2.5 py-1 rounded text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer ${statusFilter === 'critical'
                        ? 'bg-rose-500 text-white shadow-xs'
                        : 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                      }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                    Critical (&gt;80%)
                  </button>
                </div>
              </div>

              <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono flex items-center gap-2">
                <span>Showing {filteredRooms.length} of {currentComplex.rooms.length} blocks</span>
              </div>
            </div>

            {/* Dual Column Split Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Left Column Table */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-bold font-mono uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
                    {currentComplex.id} — Section A (Blocks 1 to {splitMid})
                  </span>
                  <span className="text-[11px] font-mono text-neutral-400">
                    {leftTableRooms.length} compartments
                  </span>
                </div>
                {renderRoomTable(leftTableRooms)}
              </div>

              {/* Right Column Table */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-bold font-mono uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
                    {currentComplex.id} — Section B (Blocks {splitMid + 1} to {currentComplex.rooms.length})
                  </span>
                  <span className="text-[11px] font-mono text-neutral-400">
                    {rightTableRooms.length} compartments
                  </span>
                </div>
                {renderRoomTable(rightTableRooms)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dedicated Holding Area Live CCTV Surveillance Modal */}
      {activeHoldingCam && (
        <HoldingAreaCCTVModal
          camId={activeHoldingCam.camId}
          room={
            holdingComplexes
              .flatMap(h => h.rooms)
              .find(r => r.id === activeHoldingCam.room.id) || activeHoldingCam.room
          }
          areaName={activeHoldingCam.areaName}
          sector={activeHoldingCam.sector}
          onClose={() => setActiveHoldingCam(null)}
        />
      )}
    </div>
  );
};

export const HoldingAreaView: React.FC = () => {
  return (
    <CrowdProvider>
      <HoldingAreaViewInner />
    </CrowdProvider>
  );
};
